// The ember scene's tiers and pacing, in one place. The probe picks a
// tier before any frame is drawn; the governor moves between tiers (and the
// render scale inside one) at runtime.

export type TierName = 'high' | 'medium' | 'low'

/** `gate`: the full-screen sign-in scene. `hearth`: the small fire over an empty chat. */
export type Variant = 'gate' | 'hearth'

export interface Tier {
  name: TierName
  /** Particles that form the mark. */
  embers: number
  /** Sparks rising off the mark. */
  sparks: number
  /** Out-of-focus motes drifting in front. */
  motes: number
  /** Stars (gate only). */
  stars: number
  /** Campfires out on the plain, one per agent (gate only). */
  fires: number
  /** Bloom mip levels; 0 renders straight to the canvas. */
  bloomLevels: number
  maxDpr: number
  /** Device pixels the scene may fill before the render scale drops. */
  pixelBudget: number
  minScale: number
  maxScale: number
  /** The frame budget the governor defends, ms. */
  targetMs: number
  /** 0 = the display's rate. */
  fpsCap: number
}

export const TIERS: Record<TierName, Tier> = {
  high: {
    name: 'high',
    embers: 36000,
    sparks: 2400,
    motes: 44,
    stars: 2600,
    fires: 420,
    bloomLevels: 5,
    maxDpr: 2,
    pixelBudget: 3.7e6,
    minScale: 0.55,
    maxScale: 1,
    targetMs: 16.7,
    fpsCap: 0,
  },
  medium: {
    name: 'medium',
    embers: 16000,
    sparks: 1200,
    motes: 30,
    stars: 1600,
    fires: 260,
    bloomLevels: 4,
    maxDpr: 1.5,
    pixelBudget: 1.6e6,
    minScale: 0.5,
    maxScale: 1,
    targetMs: 16.7,
    fpsCap: 0,
  },
  low: {
    name: 'low',
    embers: 6000,
    sparks: 500,
    motes: 14,
    stars: 900,
    fires: 140,
    bloomLevels: 2,
    maxDpr: 1,
    pixelBudget: 0.6e6,
    minScale: 0.5,
    maxScale: 1,
    targetMs: 33.3,
    fpsCap: 30,
  },
}

export const TIER_ORDER: readonly TierName[] = ['high', 'medium', 'low']

/** The hearth is a small fire: a fraction of the gate's particles, at most 30 fps when nobody touches it. */
export const HEARTH_SHARE = 0.42
export const IDLE_FPS = 30
export const IDLE_AFTER_MS = 12000
